import React, { useEffect, useRef, useState } from 'react';
import {
  Heart,
  Trophy,
  Globe,
  Briefcase,
  Star,
  Flame,
} from 'lucide-react';
import landingService from '../../services/landingService';
import './ReviewsShowcase.css';

/**
 * ReviewsShowcase — Notion-style social proof section
 * Layout: Title → Featured hero card (text-only) → 3×2 people card grid → Stats marquee
 */
const ReviewsShowcase = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [featured, setFeatured] = useState(null);
  const [cards, setCards] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Fetch testimonials from API
  useEffect(() => {
    const fetchTestimonials = async () => {
      const result = await landingService.getTestimonials();
      if (result.success) {
        setFeatured(result.featured || null);
        setCards(result.testimonials || []);
      }
    };
    fetchTestimonials();
  }, []);

  /* ── Bottom Marquee Stats (marketing copy — stays frontend) ── */
  const stats = [
    { icon: Flame, text: '62% of top-100 research institutions' },
    { icon: Heart, text: 'Over 50% of R1 universities' },
    { icon: Globe, text: 'Over 25K researchers worldwide' },
    { icon: Briefcase, text: '#1 research collaboration platform' },
    { icon: Trophy, text: '#1 knowledge base 3 years running' },
    { icon: Star, text: '2M+ papers shared' },
  ];

  /* doubled for seamless infinite scroll */
  const marqueeItems = [...stats, ...stats];

  return (
    <section
      ref={sectionRef}
      className={`rshow ${isVisible ? 'rshow--visible' : ''}`}
    >
      <div className="rshow__container">

        {/* ── Title ── */}
        <h2 className="rshow__title">
          Trusted by researchers who ship.
        </h2>

        {/* ── Featured Hero Card (text-only, no visual) ── */}
        {featured && (
        <div className="rshow__featured">
          <div className="rshow__featured-text">
            <div className="rshow__featured-person">
              <span className="rshow__featured-name">{featured.name}</span>
              <span className="rshow__featured-role">{featured.role}</span>
            </div>
            <blockquote className="rshow__featured-quote">
              {featured.text || featured.quote}
            </blockquote>

          </div>
        </div>
        )}

        {/* ── 3×2 Card Grid ── */}
        <div className="rshow__grid">
          {cards.map((card, i) => (
            <div
              key={card._id || i}
              className="rshow__card"
              style={{ '--card-delay': `${i * 60 + 200}ms` }}
            >
              <div className="rshow__card-person">
                <span className="rshow__card-name">{card.name}</span>
                <span className="rshow__card-role">{card.role}</span>
              </div>
              <p className="rshow__card-text">
                {card.text}
              </p>
            </div>
          ))}
        </div>

        {/* ── Stats Marquee ── */}
        <div className="rshow__marquee-wrap">
          <div className="rshow__marquee-track">
            {marqueeItems.map((stat, i) => (
              <div key={i} className="rshow__marquee-item">
                <stat.icon size={14} />
                <span>{stat.text}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ReviewsShowcase;
